import React, { ReactElement, ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import {Link} from "react-router-dom";
import {UserMenuModel} from "app/model/ua/user-menu";

export interface MegaMenuProps {
  visible?: boolean;
  menus?: UserMenuModel[] | undefined | null
  activeMenu?: UserMenuModel | undefined
  currentMenu?: UserMenuModel | undefined
  showMenuDesc?: boolean;
  // gnb: 헤더 상단 메뉴. 대메뉴에 마우스를 올리면 패널 하나가 열리고,
  //      모든 대메뉴의 하위 메뉴가 각 대메뉴 바로 아래에 세로 컬럼으로 함께 보인다.
  // full: 전체메뉴 패널 안 아코디언(하위 메뉴가 있는 항목은 클릭으로 펼치고 접는다)
  variant?: 'gnb' | 'full';
  onClick?: (selectedMenu: UserMenuModel, selectedMenusRootMenu: UserMenuModel) => void;
}

type MenuClickHandler = (selectedMenu: UserMenuModel, selectedMenusRootMenu: UserMenuModel) => void;

// line-awesome(las) 아이콘 폰트 순환 — 메뉴 개수/라벨이 역할별로 달라 텍스트 매칭 대신
// 대시보드/에너지/거래/보고서/공지/산단/설정 순으로 의미상 가까운 아이콘을 배정한다.
const MENU_ICONS = [
  "la-home",
  "la-solar-panel",
  "la-exchange-alt",
  "la-file-alt",
  "la-bullhorn",
  "la-industry",
  "la-cogs",
];

// 마우스가 메뉴 영역을 잠깐 벗어났다 돌아올 때 바로 닫히지 않도록 기다린다
const GNB_HIDE_DELAY_MS = 50;

// 메뉴 이름으로 아이콘을 정해 둔 대메뉴. 없으면 순서대로 MENU_ICONS 를 돌려 쓴다.
// "svg:" 로 시작하면 line-awesome 에 없는 아이콘이라 아래 MENU_SVG_ICONS 로 직접 그린다
const MENU_ICONS_BY_NAME: Record<string, string> = {
  "에너지 현황": "svg:monitor-chart",
  // 재생에너지 이행 = 잎, 거래 = 주고받는 화살표
  "RE100 관리": "la-leaf",
  "RE100": "la-leaf",
  "장외거래": "la-exchange-alt",
};

// 모니터 안에 꺾은선 차트: line-awesome 선 아이콘과 굵기·크기(1em)를 맞춘다
const MENU_SVG_ICONS: Record<string, ReactElement> = {
  "monitor-chart": (
    <svg className="las menu-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <rect x="2.5" y="3.5" width="19" height="13" rx="1.5" />
      <path d="M8 20.5h8M12 16.5v4" />
      <path d="M6.5 12.5l3.5-3.5 2.5 2 5-4.5" />
    </svg>
  ),
};

function MenuIcon(props: { index: number; menuNm?: string }): ReactElement {
  const named = props.menuNm ? MENU_ICONS_BY_NAME[props.menuNm.trim()] : undefined;
  if (named?.startsWith('svg:')) {
    return MENU_SVG_ICONS[named.slice(4)];
  }
  const icon = named ?? MENU_ICONS[props.index % MENU_ICONS.length];
  return <i className={`las ${icon}`} aria-hidden="true"></i>;
}

function classNames(...names: (string | false | undefined)[]): string {
  return names.filter(Boolean).join(' ');
}

function hasSubMenus(menu: UserMenuModel): boolean {
  return (menu.subMenus?.length ?? 0) > 0;
}

function isDepth02Current(child: UserMenuModel, currentMenu?: UserMenuModel): boolean {
  return currentMenu?.menuId === child.menuId ||
    (child.subMenus?.some((s) => s.menuId === currentMenu?.menuId) ?? false);
}

// 전체메뉴 아코디언: 높이를 grid-template-rows(0fr ↔ 1fr) 전환으로 펼치고 접는다 (layout.css)
function Collapse(props: { open: boolean; children: ReactNode }): ReactElement {
  return (
    <div className={classNames('fm-collapse', props.open && 'is-open')}>
      <div className="fm-collapse-inner">{props.children}</div>
    </div>
  );
}

function Depth03List(props: {
  items?: UserMenuModel[];
  rootMenu: UserMenuModel;
  currentMenu?: UserMenuModel;
  onClick?: MenuClickHandler;
}): ReactElement | null {
  const { items, rootMenu, currentMenu, onClick } = props;
  if (!items || items.length === 0) return null;
  return (
    <ul className="depth03">
      {items.map((sub: UserMenuModel) => (
        <li key={sub.menuId} className={currentMenu?.menuId === sub.menuId ? 'on' : ''}>
          <Link
            to="#"
            onClick={(e) => {
              e.preventDefault();
              onClick?.(sub, rootMenu);
            }}
          >
            <span>{sub.menuNm}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function GnbMenu(props: {
  items: UserMenuModel[];
  activeMenu?: UserMenuModel;
  currentMenu?: UserMenuModel;
  onClick?: MenuClickHandler;
}): ReactElement {
  const { items, activeMenu, currentMenu, onClick } = props;
  // 마우스가 올라가 있는 대메뉴. 값이 있으면 패널이 열린다
  const [hoverMenuId, setHoverMenuId] = useState<string>();
  // 가장 긴 하위 메뉴 컬럼에 맞춘 패널 높이(px)
  const [panelHeight, setPanelHeight] = useState<number>();
  const hideTimer = useRef<ReturnType<typeof setTimeout>>();
  const depth01Ref = useRef<HTMLUListElement>(null);
  const isOpen = hoverMenuId !== undefined;
  // 컬럼 스포트라이트는 하위 메뉴가 있는 대메뉴에만 켠다 (보고서 같은 단일 메뉴는 패널만 열어 둔다)
  const spotMenuId = items.find((menu) => menu.menuId === hoverMenuId && hasSubMenus(menu))?.menuId;

  useEffect(() => () => clearTimeout(hideTimer.current), []);

  // 컬럼은 대메뉴 li 에 absolute 로 붙어 있어 패널 높이를 CSS 로 정할 수 없으므로 직접 잰다.
  // 컬럼 자체는 이 높이(--gnb-panel-h)로 늘어나므로, 안쪽 목록 높이에 컬럼 여백을 더해 잰다
  useLayoutEffect(() => {
    const columns = Array.from(depth01Ref.current?.querySelectorAll<HTMLElement>('.depth-col') ?? []);
    if (columns.length === 0) {
      setPanelHeight(undefined);
      return;
    }
    const style = getComputedStyle(columns[0]);
    const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const tallest = columns.reduce((max, col) => {
      const list = col.querySelector<HTMLElement>('.depth02');
      return Math.max(max, list ? list.offsetHeight : 0);
    }, 0);
    setPanelHeight(tallest > 0 ? Math.ceil(tallest + padding) : undefined);
  }, [items, isOpen]);

  function open(menuId?: string) {
    clearTimeout(hideTimer.current);
    setHoverMenuId(menuId);
  }

  function closeLater() {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setHoverMenuId(undefined), GNB_HIDE_DELAY_MS);
  }

  function select(menu: UserMenuModel, rootMenu: UserMenuModel) {
    clearTimeout(hideTimer.current);
    setHoverMenuId(undefined);
    onClick?.(menu, rootMenu);
  }

  return (
    <div
      className={classNames('gnb', isOpen && 'is-open', spotMenuId !== undefined && 'has-spot')}
      style={panelHeight ? ({ '--gnb-panel-h': `${panelHeight}px` } as React.CSSProperties) : undefined}
      onMouseLeave={closeLater}
    >
      <div className="overlay"></div>
      <div className="gnb-panel"></div>
      <ul className="depth01" ref={depth01Ref}>
        {items.map((menu: UserMenuModel, index: number) => (
          <li
            key={menu.menuId}
            className={classNames(
              activeMenu?.menuId === menu.menuId && 'on',
              hoverMenuId === menu.menuId && 'is-hover',
              spotMenuId === menu.menuId && 'is-lit'
            )}
            onMouseEnter={() => open(menu.menuId)}
          >
            <a
              href="#"
              className="menu"
              onClick={(e) => {
                e.preventDefault();
                if (!hasSubMenus(menu)) {
                  select(menu, menu);
                }
              }}
            >
              <MenuIcon index={index} menuNm={menu.menuNm} />
              <span>{menu.menuNm}</span>
            </a>
            <div className="depth-col">
              {hasSubMenus(menu) && (
                <ul className="depth02">
                  {menu.subMenus!.map((child: UserMenuModel) => (
                    <li key={child.menuId} className={isDepth02Current(child, currentMenu) ? 'on' : ''}>
                      <Link
                        to="#"
                        onClick={(e) => {
                          e.preventDefault();
                          select(child, menu);
                        }}
                      >
                        <span>{child.menuNm}</span>
                      </Link>
                      <Depth03List
                        items={child.subMenus}
                        rootMenu={menu}
                        currentMenu={currentMenu}
                        onClick={select}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FullMenu(props: {
  items: UserMenuModel[];
  activeMenu?: UserMenuModel;
  currentMenu?: UserMenuModel;
  onClick?: MenuClickHandler;
}): ReactElement {
  const { items, activeMenu, currentMenu, onClick } = props;
  // 펼친 대메뉴 / 펼친 2단 메뉴 (형제끼리는 하나만 펼친다)
  const [openDepth01Id, setOpenDepth01Id] = useState<string>();
  const [openDepth02Id, setOpenDepth02Id] = useState<string>();

  return (
    <div className="gnb">
      <ul className="depth01">
        {items.map((menu: UserMenuModel, index: number) => {
          const hasChildren = hasSubMenus(menu);
          const isOpen = openDepth01Id === menu.menuId;
          return (
            <li
              key={menu.menuId}
              className={classNames(activeMenu?.menuId === menu.menuId && 'on', hasChildren && 'dep', isOpen && 'active open')}
            >
              <a
                href="#"
                className="menu"
                onClick={(e) => {
                  e.preventDefault();
                  if (hasChildren) {
                    setOpenDepth01Id(isOpen ? undefined : menu.menuId);
                  } else {
                    onClick?.(menu, menu);
                  }
                }}
              >
                <MenuIcon index={index} menuNm={menu.menuNm} />
                <span>{menu.menuNm}</span>
              </a>
              {hasChildren && (
                <div className="depth-wrap">
                  <div className="w-1600">
                    <Collapse open={isOpen}>
                      <ul className="depth02">
                        {menu.subMenus!.map((child: UserMenuModel) => {
                          const hasDepth03 = hasSubMenus(child);
                          const isDepth02Open = openDepth02Id === child.menuId;
                          const depth03 = (
                            <Depth03List
                              items={child.subMenus}
                              rootMenu={menu}
                              currentMenu={currentMenu}
                              onClick={onClick}
                            />
                          );
                          return (
                            <li
                              key={child.menuId}
                              className={classNames(
                                isDepth02Current(child, currentMenu) && 'on',
                                hasDepth03 && 'dep',
                                isDepth02Open && 'active open'
                              )}
                            >
                              <Link
                                to="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (hasDepth03) {
                                    setOpenDepth02Id(isDepth02Open ? undefined : child.menuId);
                                  } else {
                                    onClick?.(child, menu);
                                  }
                                }}
                              >
                                <span>{child.menuNm}</span>
                              </Link>
                              {hasDepth03 ? <Collapse open={isDepth02Open}>{depth03}</Collapse> : depth03}
                            </li>
                          );
                        })}
                      </ul>
                    </Collapse>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function MegaMenu(props: MegaMenuProps) {
  const {
    menus,
    visible = props.visible ?? true,
    activeMenu,
    currentMenu,
    variant = 'gnb',
    onClick
  } = props;

  if (!visible) {
    return null;
  }

  const items = menus ?? [];
  return variant === 'full'
    ? <FullMenu items={items} activeMenu={activeMenu} currentMenu={currentMenu} onClick={onClick} />
    : <GnbMenu items={items} activeMenu={activeMenu} currentMenu={currentMenu} onClick={onClick} />;
}
